import { useEffect, useRef, useState } from 'react';

import { logError } from 'services/logging/logging';
import { GenericError } from 'support/helpers/errors/errors';

import { ConfigItem } from '../schemableFormNavigation/types';
import { SuggestionNavigableItem } from './types';
import { SuggestionChangesResponse } from './workerTypes';

const EMPTY_ITEMS: Array<SuggestionNavigableItem> = [];

export const useSuggestionChangesWorker = (
  baseData: Record<string, unknown> | undefined,
  revisedData: Record<string, unknown> | undefined,
  uiConfig: Array<ConfigItem> | undefined,
): Array<SuggestionNavigableItem> => {
  const [items, setItems] = useState<Array<SuggestionNavigableItem>>(EMPTY_ITEMS);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    const worker = new Worker(new URL('./suggestionChangesWorkerBootstrap.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;

    worker.addEventListener('message', (event: MessageEvent<SuggestionChangesResponse>) => {
      if (event.data.type === 'error') {
        logError(
          new GenericError(event.data.message, {
            source: 'suggestionChangesWorker',
            stack: event.data.stack,
            name: event.data.name,
            isGlobalError: event.data.isGlobalError,
          }),
        );
        return;
      }

      setItems(event.data.items);
    });

    worker.addEventListener('error', (event: ErrorEvent) => {
      logError(
        new GenericError(event.message || 'Suggestion changes worker error', {
          source: 'suggestionChangesWorker',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        }),
      );
    });

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!baseData || !revisedData || !workerRef.current) {
      setItems(EMPTY_ITEMS);
      return;
    }

    workerRef.current.postMessage({ baseData, revisedData, uiConfig });
  }, [baseData, revisedData, uiConfig]);

  return items;
};
